import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useEventStore, useObservableEagerMemo } from "applesauce-react/hooks";
import { firstValueFrom, timeout } from "rxjs";
import { NostrEvent } from "nostr-tools";

import { queryKeys } from "../../helpers/query-keys";
import { replaceableLoader, profileLoader } from "../../services/loaders";

interface ReplaceablePointer {
  kind: number;
  pubkey: string;
  identifier?: string;
}

interface UseNostrReplaceableOptions {
  /** Override default staleTime (5 min) */
  staleTime?: number;
  /** Poll interval in ms (default: none) */
  refetchInterval?: number;
  /** Use the profile loader (with lookup relays) instead of the standard replaceable loader */
  useProfileLoader?: boolean;
}

/**
 * Bridge hook: TanStack Query manages fetch timing, EventStore holds the data.
 *
 * The queryFn calls the applesauce loader, waits for it to complete,
 * and returns a receipt. The actual event data comes from
 * `useObservableEagerMemo(eventStore.replaceable(...))`.
 */
export default function useNostrReplaceable(
  pointer: ReplaceablePointer | undefined,
  opts?: UseNostrReplaceableOptions,
) {
  const eventStore = useEventStore();

  // TanStack manages fetch scheduling
  const loader = opts?.useProfileLoader ? profileLoader : replaceableLoader;
  const queryOpts: UseQueryOptions<{ fetched: boolean; at: number }> = {
    queryKey: queryKeys.nostr.replaceable(
      pointer?.kind ?? 0,
      pointer?.pubkey ?? "",
      pointer?.identifier,
    ),
    queryFn: async () => {
      if (!pointer) throw new Error("No pointer");
      const observable = loader({
        kind: pointer.kind,
        pubkey: pointer.pubkey,
        identifier: pointer.identifier,
      }).pipe(timeout(15_000));
      await firstValueFrom(observable).catch(() => {
        // Loader may complete empty (no event found) — that's OK
      });
      return { fetched: true, at: Date.now() };
    },
    enabled: !!pointer,
    staleTime: opts?.staleTime,
    refetchInterval: opts?.refetchInterval,
  };

  const { isLoading, isError, isFetching, refetch } = useQuery(queryOpts);

  // Event data from EventStore (the source of truth)
  const event = useObservableEagerMemo(() => {
    if (!pointer) return undefined;
    return eventStore.replaceable(pointer);
  }, [pointer?.kind, pointer?.pubkey, pointer?.identifier, eventStore]) as NostrEvent | undefined;

  return { event, isLoading, isError, isFetching, refetch };
}
